import { useLogout } from "@/features/auth/auth.hooks";
import { useAuthStore } from "@/features/auth/auth.store";
import {
	executionKeys,
	useGetExecutionStatus,
	useStartExecution,
	useStopExecution,
} from "@/features/execution";
import {
	paymentApi,
	getBillingKey,
	chargeBilling,
	registerBillingKeyWithAuth,
} from "@/features/payment/payment.api";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { WorkflowDatabaseModal } from "@/features/workflow-db";
import { workflowEnvKeys, useGetWorkflowEnv, useUpdateWorkflowEnv } from "@/features/workflow-env";
import {
	workflowLogApi,
	workflowLogKeys,
	useGetErrorLogs,
	useGetExecutionLogs,
	useRefreshLogs,
} from "@/features/workflow-log";
import {
	workflowUploadKeys,
	useDeleteWorkflowUpload,
	useGetDownloadUrl,
	useGetWorkflowUploads,
	useUploadWorkflowFile,
} from "@/features/workflow-upload";
import {
	type AddWorkflowCanvasElementInput,
	NodePropertyModal,
	WorkflowCanvas,
	type WorkflowCanvasElement,
	type WorkflowCanvasHandle,
	useWorkflowCanvasElements,
	useWorkflowCanvasSync,
} from "@/features/workflow/ui";
import {
	workflowKeys,
	useCreateWorkflow,
	useDeleteWorkflow,
	useGetWorkflow,
	useGetWorkflows,
} from "@/features/workflow/workflow.hooks";
import {
	CardHeader,
	ConfirmModal,
	ErrorCard,
	GyulCard,
	GyulRefillModal,
	LoggerCard,
	TextInputField,
	UserCard,
	WorkflowCardDropdown,
	WorkflowHeader,
} from "@/shared/ui/composites";
import { formatRelativeTime } from "@/shared/lib/format-relative-time";
import { useViewportStore } from "@/shared/stores/viewport.store";
import { Button, HStack, Icon, Modal, Spinner, VStack } from "@/shared/ui/primitives";
import { userKeys } from "@/features/user/user.hooks";
import { Outlet, createRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { workflowRoute, saveLastWorkflowId } from "../route";

const WORKFLOW_DND_TYPE = "application/x-workflow-node";
const TOSS_CLIENT_KEY =
	import.meta.env.VITE_TOSS_CLIENT_KEY || "test_ck_0RnYX2w532DZKROLneEPrNeyqApQ";
const SOLAPI_LINK_PATH = "/oauth/solapi";
const GOOGLE_CONNECT_LINK_PATH = "/oauth/google/connect";

interface EnvSettingRow {
	id: string;
	key: string;
	value: string;
}

const createEnvSettingRow = (): EnvSettingRow => ({
	id:
		typeof globalThis.crypto?.randomUUID === "function"
			? globalThis.crypto.randomUUID()
			: `env-${Date.now()}-${Math.random().toString(16).slice(2)}`,
	key: "",
	value: "",
});

type SolapiPopupMessage = {
	type?: string;
	status?: string;
	returnUrl?: string | null;
};

function isSolapiErrorMessage(title: string, description: string): boolean {
	const normalized = `${title} ${description}`.trim().toLowerCase();
	return normalized.includes("솔라피") || normalized.includes("solapi");
}

function isGoogleAuthErrorMessage(title: string, description: string): boolean {
	const normalized = `${title} ${description}`.trim().toLowerCase();
	return (
		normalized.includes("구글") ||
		normalized.includes("google") ||
		normalized.includes("gmail") ||
		normalized.includes("drive") ||
		normalized.includes("sheet") ||
		normalized.includes("youtube")
	);
}

function getErrorMoveUrl(
	title: string,
	description: string,
	url: string | null | undefined,
): string | undefined {
	if (isSolapiErrorMessage(title, description)) {
		return SOLAPI_LINK_PATH;
	}
	if (isGoogleAuthErrorMessage(title, description)) {
		return GOOGLE_CONNECT_LINK_PATH;
	}
	const trimmedUrl = typeof url === "string" ? url.trim() : "";
	return trimmedUrl || undefined;
}

const Desktop = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const solapiPopupRef = useRef<Window | null>(null);
	const solapiPopupPollRef = useRef<number | null>(null);
	const { id } = workflowIdRoute.useParams();
	const isMobile = useViewportStore((s) => s.isMobile);
	const [activeTab, setActiveTab] = useState<"controls" | "canvas">("controls");

	useEffect(() => {
		saveLastWorkflowId(id);
	}, [id]);

	const user = useAuthStore((s) => s.user);
	const { data: workflow, isSuccess: isWorkflowLoaded } = useGetWorkflow(id);
	const { data: workflowsData } = useGetWorkflows();
	const createWorkflow = useCreateWorkflow();
	const deleteWorkflow = useDeleteWorkflow();
	const logout = useLogout();

	const { data: executionStatusData } = useGetExecutionStatus(id, { polling: true });
	const startExecution = useStartExecution();
	const stopExecution = useStopExecution();
	const isExecutionRunning = executionStatusData?.status === "running";

	const { data: _envData } = useGetWorkflowEnv(id);
	const updateEnv = useUpdateWorkflowEnv();

	const uploadFile = useUploadWorkflowFile();
	const { data: uploadsData } = useGetWorkflowUploads(id);
	const deleteUpload = useDeleteWorkflowUpload();
	const getDownloadUrl = useGetDownloadUrl();

	const { data: executionLogsData, isFetching: isLogsFetching } = useGetExecutionLogs(id, { polling: isExecutionRunning });
	const { data: errorLogsData } = useGetErrorLogs(id, { polling: isExecutionRunning });
	const refreshLogs = useRefreshLogs(id);
	const hasErrors = (errorLogsData?.errors ?? []).length > 0;

	const prevIsRunningRef = useRef(isExecutionRunning);
	useEffect(() => {
		if (prevIsRunningRef.current && !isExecutionRunning) {
			refreshLogs();
		}
		prevIsRunningRef.current = isExecutionRunning;
	}, [isExecutionRunning, refreshLogs]);

	const [isNewErrorModalOpen, setIsNewErrorModalOpen] = useState(false);
	const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
	const [isRepairConfirmModalOpen, setIsRepairConfirmModalOpen] = useState(false);
	const [isLogoutConfirmModalOpen, setIsLogoutConfirmModalOpen] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
	const [isControlDropdownOpen, setIsControlDropdownOpen] = useState(false);
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const [isEnvModalOpen, setIsEnvModalOpen] = useState(false);
	const [isDatabaseModalOpen, setIsDatabaseModalOpen] = useState(false);
	const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
	const [isRefillModalOpen, setIsRefillModalOpen] = useState(false);
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [envSettings, setEnvSettings] = useState<EnvSettingRow[]>([]);
	const [isNotImplementedModalOpen, setIsNotImplementedModalOpen] = useState(false);
	const [hasBillingKey, setHasBillingKey] = useState(false);
	const [refillLoading, setRefillLoading] = useState(false);
	const processedPaymentRef = useRef(false);
	const [editingElement, setEditingElement] = useState<WorkflowCanvasElement | null>(null);
	const canvasController = useWorkflowCanvasElements();
	const { setElements, setEdges } = canvasController;
	const canvasRef = useRef<WorkflowCanvasHandle>(null);
	const actionDropdownRef = useRef<HTMLDivElement | null>(null);
	const actionButtonRef = useRef<HTMLButtonElement | null>(null);
	const controlDropdownRef = useRef<HTMLDivElement | null>(null);
	const controlButtonRef = useRef<HTMLButtonElement | null>(null);
	const envListRef = useRef<HTMLDivElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const dragCounterRef = useRef(0);

	const handleCloseNodePropertyModal = () => {
		setEditingElement(null);
	};

	const handleSaveNodeProperty = (nextElement: WorkflowCanvasElement) => {
		canvasController.updateElement(nextElement.id, () => nextElement);
		setEditingElement(null);
	};

	const { syncCount, resync } = useWorkflowCanvasSync(id, workflow, isWorkflowLoaded, setElements, setEdges);

	const handleRefresh = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
		queryClient.invalidateQueries({ queryKey: executionKeys.status(id) });
		queryClient.invalidateQueries({ queryKey: workflowEnvKeys.env(id) });
		queryClient.invalidateQueries({ queryKey: workflowUploadKeys.list(id) });
		refreshLogs();
		resync();
	}, [queryClient, id, refreshLogs, resync]);

	useEffect(() => {
		if (syncCount === 0) return;
		canvasRef.current?.fitToContent();
	}, [syncCount]);

	// Fetch billing key status on mount
	useEffect(() => {
		getBillingKey()
			.then((key) => setHasBillingKey(!!key))
			.catch(() => {});
	}, []);

	// Handle payment/billing redirect
	useEffect(() => {
		if (processedPaymentRef.current) return;
		const params = new URLSearchParams(window.location.search);

		const payment = params.get("payment");
		if (payment === "success") {
			processedPaymentRef.current = true;
			const paymentKey = params.get("paymentKey");
			const orderId = params.get("orderId");
			const amount = Number(params.get("amount"));
			if (paymentKey && orderId && amount) {
				paymentApi
					.confirmPayment({ paymentKey, orderId, amount })
					.then(() => queryClient.invalidateQueries({ queryKey: userKeys.me() }))
					.catch(() => {});
			}
			window.history.replaceState({}, "", `/workflow/${id}`);
			return;
		}
		if (payment === "fail") {
			processedPaymentRef.current = true;
			window.history.replaceState({}, "", `/workflow/${id}`);
			return;
		}

		const billing = params.get("billing");
		if (billing === "success") {
			processedPaymentRef.current = true;
			const authKey = params.get("authKey");
			const customerKey = params.get("customerKey");
			if (authKey && customerKey) {
				registerBillingKeyWithAuth(authKey, customerKey).then((r) => {
					if (r.success) setHasBillingKey(true);
				});
			}
			window.history.replaceState({}, "", `/workflow/${id}`);
			return;
		}
		if (billing === "fail") {
			processedPaymentRef.current = true;
			window.history.replaceState({}, "", `/workflow/${id}`);
		}
	}, [id]);

	// Handle OAuth redirect return
	useEffect(() => {
		const returnUrl = sessionStorage.getItem("oauth_return_url");
		if (!returnUrl) return;
		sessionStorage.removeItem("oauth_return_url");
		queryClient.invalidateQueries({ queryKey: workflowLogKeys.errorLogs(id) });
	}, [id, queryClient]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;
			const data = event.data as SolapiPopupMessage | undefined;
			if (data?.type !== "inflect:solapi-oauth-complete" || data.status !== "linked") return;

			if (solapiPopupPollRef.current) {
				window.clearInterval(solapiPopupPollRef.current);
				solapiPopupPollRef.current = null;
			}
			solapiPopupRef.current = null;
			void queryClient.invalidateQueries({ queryKey: workflowLogKeys.errorLogs(id) });
			const targetUrl =
				typeof data.returnUrl === "string" && data.returnUrl.startsWith("/")
					? data.returnUrl
					: `/workflow/${id}`;
			const currentUrl = `${window.location.pathname}${window.location.search}`;
			if (currentUrl === targetUrl) {
				window.location.reload();
				return;
			}
			window.location.assign(targetUrl);
		};

		window.addEventListener("message", handleMessage);
		return () => {
			window.removeEventListener("message", handleMessage);
			if (solapiPopupPollRef.current) {
				window.clearInterval(solapiPopupPollRef.current);
				solapiPopupPollRef.current = null;
			}
		};
	}, [id, queryClient]);

	const startSolapiPopupPolling = useCallback(
		(popup: Window | null, returnUrl: string) => {
			if (solapiPopupPollRef.current) {
				window.clearInterval(solapiPopupPollRef.current);
			}
			solapiPopupRef.current = popup;
			solapiPopupPollRef.current = window.setInterval(async () => {
				try {
					const response = await queryClient.fetchQuery({
						queryKey: workflowLogKeys.errorLogs(id),
						queryFn: () => workflowLogApi.getErrorLogs(id),
					});
					const hasSolapiError = (response.errors ?? []).some((error) =>
						isSolapiErrorMessage(error.title, error.description),
					);
					if (hasSolapiError) {
						if (popup?.closed) {
							window.clearInterval(solapiPopupPollRef.current!);
							solapiPopupPollRef.current = null;
							solapiPopupRef.current = null;
						}
						return;
					}

					if (popup && !popup.closed) {
						popup.close();
					}
					window.clearInterval(solapiPopupPollRef.current!);
					solapiPopupPollRef.current = null;
					solapiPopupRef.current = null;
					window.location.assign(returnUrl);
				} catch {
					if (popup?.closed) {
						window.clearInterval(solapiPopupPollRef.current!);
						solapiPopupPollRef.current = null;
						solapiPopupRef.current = null;
					}
				}
			}, 1200);
		},
		[id, queryClient],
	);

	const handleGyulPurchase = useCallback(
		async (count: number, _price: number) => {
			if (hasBillingKey) {
				// Auto-charge: charge via billing key without Toss window
				setRefillLoading(true);
				const result = await chargeBilling(count);
				setRefillLoading(false);
				setIsRefillModalOpen(false);
				if (result.success) {
					queryClient.invalidateQueries({ queryKey: userKeys.me() });
				}
				return;
			}
			// Regular payment: open Toss payment window
			try {
				setRefillLoading(true);
				const order = await paymentApi.requestGyulPayment({ amount: count });
				const toss = await loadTossPayments(TOSS_CLIENT_KEY);
				const origin = window.location.origin;
				await toss.payment({ customerKey: user?.id ?? "" }).requestPayment({
					method: "CARD",
					amount: { value: order.amount, currency: "KRW" },
					orderId: order.orderId,
					orderName: order.orderName,
					successUrl: `${origin}/workflow/${id}?payment=success`,
					failUrl: `${origin}/workflow/${id}?payment=fail`,
				});
			} catch {
				// User cancelled or error
			} finally {
				setRefillLoading(false);
			}
		},
		[hasBillingKey, id, user?.id, queryClient],
	);

	const handleAutoChargeToggle = useCallback(async () => {
		if (hasBillingKey) return;
		// Open Toss billing auth window for card registration
		try {
			const toss = await loadTossPayments(TOSS_CLIENT_KEY);
			const payment = toss.payment({ customerKey: user?.id ?? "" });
			const origin = window.location.origin;
			await payment.requestBillingAuth({
				method: "CARD",
				successUrl: `${origin}/workflow/${id}?billing=success`,
				failUrl: `${origin}/workflow/${id}?billing=fail`,
			});
		} catch {
			// User cancelled or error
		}
	}, [hasBillingKey, id, user?.id]);

	const handleAddEnvSetting = () => {
		setEnvSettings((current) => [...current, createEnvSettingRow()]);

		requestAnimationFrame(() => {
			const listNode = envListRef.current;
			if (!listNode) {
				return;
			}

			listNode.scrollTo({ top: listNode.scrollHeight, behavior: "smooth" });
		});
	};

	const handleUpdateEnvSetting = (id: string, field: "key" | "value", value: string) => {
		setEnvSettings((current) =>
			current.map((item) => {
				if (item.id !== id) {
					return item;
				}

				return {
					...item,
					[field]: value,
				};
			}),
		);
	};

	const handleDeleteEnvSetting = (id: string) => {
		setEnvSettings((current) => current.filter((item) => item.id !== id));
	};

	const MAX_FILE_SIZE = 20 * 1024 * 1024;

	const handleFileSelect = (files: FileList | null) => {
		if (!files || files.length === 0) return;
		const file = files[0];
		if (file.size > MAX_FILE_SIZE) {
			setUploadError(t("workflow.fileSizeLimit", { size: "20MB" }));
			return;
		}
		setUploadError(null);
		setUploadedFiles([file]);
	};

const handleFileDrop = (event: React.DragEvent) => {
		event.preventDefault();
		dragCounterRef.current = 0;
		setIsDraggingFile(false);
		handleFileSelect(event.dataTransfer.files);
	};

	const handleFileDragOver = (event: React.DragEvent) => {
		event.preventDefault();
	};

	const handleFileDragEnter = (event: React.DragEvent) => {
		event.preventDefault();
		dragCounterRef.current += 1;
		setIsDraggingFile(true);
	};

	const handleFileDragLeave = (event: React.DragEvent) => {
		event.preventDefault();
		dragCounterRef.current -= 1;
		if (dragCounterRef.current === 0) {
			setIsDraggingFile(false);
		}
	};

	const actionModules: {
		label: string;
		gyulAmount: number;
		payload: AddWorkflowCanvasElementInput;
	}[] = [
		{
			label: "HTTP",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "http",
					name: "HTTP",
					description: "Call HTTP endpoint",
					gyulAmount: 0,
					method: "GET",
					timeout: 30000,
				},
			},
		},
		{
			label: "SMS",
			gyulAmount: 1,
			payload: {
				kind: "action",
				data: { moduleType: "sms", name: "SMS", description: "Send text message", gyulAmount: 1 },
			},
		},
		{
			label: "Gmail",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "gmail",
					name: "Gmail",
					description: "Send email with Gmail",
					gyulAmount: 0,
				},
			},
		},
		{
			label: "Google Drive",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "google-drive",
					name: "Google Drive",
					description: "Read/write drive files",
					gyulAmount: 0,
					googleDriveOperation: "list",
				},
			},
		},
		{
			label: "Google Sheet",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "google-sheet",
					name: "Google Sheet",
					description: "Read/write sheet rows",
					gyulAmount: 0,
					googleSheetOperation: "read",
				},
			},
		},
		{
			label: "Google Form",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "google-form",
					name: "Google Form",
					description: "Create or manage form",
					gyulAmount: 0,
					googleFormOperation: "create",
				},
			},
		},
		{
			label: "YouTube",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "youtube",
					name: "YouTube",
					description: "Upload video to YouTube",
					gyulAmount: 0,
					youtubePrivacyStatus: "private",
					youtubeMadeForKids: false,
				},
			},
		},
		{
			label: "Transform",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "transform",
					name: "Transform",
					description: "Transform payload data",
					gyulAmount: 0,
					transformFrom: "json",
					transformTo: "json",
				},
			},
		},
		{
			label: "Latex",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "latex",
					name: "Latex",
					description: "Render LaTeX output",
					gyulAmount: 0,
				},
			},
		},
		{
			label: "Crawling",
			gyulAmount: 1,
			payload: {
				kind: "action",
				data: {
					moduleType: "crawl",
					name: "Crawling",
					description: "Collect web page data",
					gyulAmount: 1,
					timeout: 15000,
				},
			},
		},
		{
			label: "AI",
			gyulAmount: 2,
			payload: {
				kind: "action",
				data: {
					moduleType: "ai",
					name: "AI",
					description: "Run AI model action",
					gyulAmount: 2,
					aiResponseType: "natural",
				},
			},
		},
		{
			label: "DB",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "db",
					name: "DB",
					description: "Execute database query",
					gyulAmount: 0,
					dbOperation: "read",
				},
			},
		},
		{
			label: "ENV",
			gyulAmount: 0,
			payload: {
				kind: "action",
				data: {
					moduleType: "env",
					name: "ENV",
					description: "Read/write env variable",
					gyulAmount: 0,
					envOperation: "get",
				},
			},
		},
	];

	const controlModules: { label: string; payload: AddWorkflowCanvasElementInput }[] = [
		{
			label: "Condition",
			payload: {
				kind: "control",
				data: {
					moduleType: "condition",
					name: "Condition",
					description: "Evaluate rule",
				},
			},
		},
		{
			label: "Loop",
			payload: {
				kind: "control",
				data: {
					moduleType: "loop",
					name: "Loop",
					description: "Repeat until exit",
					loopAlias: "item",
				},
			},
		},
		{
			label: "Delay",
			payload: {
				kind: "control",
				data: {
					moduleType: "delay",
					name: "Delay",
					description: "Wait for duration",
				},
			},
		},
		{
			label: "Schedule",
			payload: {
				kind: "control",
				data: {
					moduleType: "schedule",
					name: "Schedule",
					description: "Run by schedule",
				},
			},
		},
	];

	const workflows = workflowsData?.workflows ?? [];

	const handleNewWorkflow = () => {
		createWorkflow.mutate(
			{ name: t("workflow.defaultName") },
			{
				onSuccess: (wf) =>
					navigate({ to: "/workflow/$id", params: { id: wf.id }, replace: true }),
				onError: () => setIsNewErrorModalOpen(true),
			},
		);
	};

	const handleDeleteWorkflow = () => {
		if (!deleteTargetId) return;
		deleteWorkflow.mutate(deleteTargetId, {
			onSuccess: () => {
				setDeleteTargetId(null);
				if (deleteTargetId === id) {
					navigate({ to: "/workflow", replace: true });
				}
			},
		});
	};

	const handleLogout = () => {
		logout.mutate(undefined, {
			onSuccess: () => navigate({ to: "/login", replace: true }),
		});
	};

	useEffect(() => {
		const html = document.documentElement;
		const body = document.body;

		const previousHtmlOverscroll = html.style.overscrollBehavior;
		const previousBodyOverscroll = body.style.overscrollBehavior;

		html.style.overscrollBehavior = "none";
		body.style.overscrollBehavior = "none";

		const handleWheel = (event: WheelEvent) => {
			if (event.ctrlKey || event.metaKey) {
				event.preventDefault();
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (!(event.ctrlKey || event.metaKey)) {
				return;
			}

			if (
				event.key === "+" ||
				event.key === "=" ||
				event.key === "-" ||
				event.key === "_" ||
				event.key === "0"
			) {
				event.preventDefault();
			}
		};

		const preventGesture = (event: Event) => {
			event.preventDefault();
		};

		window.addEventListener("wheel", handleWheel, { passive: false });
		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("gesturestart", preventGesture);
		window.addEventListener("gesturechange", preventGesture);
		window.addEventListener("gestureend", preventGesture);

		return () => {
			html.style.overscrollBehavior = previousHtmlOverscroll;
			body.style.overscrollBehavior = previousBodyOverscroll;

			window.removeEventListener("wheel", handleWheel);
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("gesturestart", preventGesture);
			window.removeEventListener("gesturechange", preventGesture);
			window.removeEventListener("gestureend", preventGesture);
		};
	}, []);

	useEffect(() => {
		if (!isControlDropdownOpen && !isActionDropdownOpen) {
			return;
		}

		const handlePointerDown = (event: PointerEvent) => {
			const targetNode = event.target as Node | null;
			if (!targetNode) {
				return;
			}

			if (actionDropdownRef.current?.contains(targetNode)) {
				return;
			}

			if (actionButtonRef.current?.contains(targetNode)) {
				return;
			}

			if (controlDropdownRef.current?.contains(targetNode)) {
				return;
			}

			if (controlButtonRef.current?.contains(targetNode)) {
				return;
			}

			setIsActionDropdownOpen(false);
			setIsControlDropdownOpen(false);
		};

		document.addEventListener("pointerdown", handlePointerDown, true);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown, true);
		};
	}, [isActionDropdownOpen, isControlDropdownOpen]);

	return (
		<HStack className={`w-full h-full bg-bg-weak ${isMobile ? "flex-col" : ""}`}>
			{isDatabaseModalOpen ? (
				<WorkflowDatabaseModal
					isOpen={isDatabaseModalOpen}
					workflowId={id}
					onClose={() => setIsDatabaseModalOpen(false)}
				/>
			) : null}
			<GyulRefillModal
				isOpen={isRefillModalOpen}
				onClose={() => setIsRefillModalOpen(false)}
				onPurchase={handleGyulPurchase}
				autoChargeEnabled={hasBillingKey}
				onAutoChargeToggle={handleAutoChargeToggle}
				isLoading={refillLoading}
			/>
			<Modal
				className="w-full max-w-[520px]"
				isOpen={isFileUploadModalOpen}
				onClose={() => {
					setIsFileUploadModalOpen(false);
					setIsDraggingFile(false);
					dragCounterRef.current = 0;
				}}
			>
				<VStack className="w-full max-h-[82vh] rounded-[24px] bg-bg-weak/95 border-outline p-[20px] gap-[16px] items-start backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
					<HStack className="w-full justify-between items-center p-[4px]">
						<VStack className="items-start gap-[2px]">
							<span className="text-xl font-semibold text-ct-strong">{t("workflow.fileUpload")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.fileUploadDescription")}</span>
						</VStack>
						<button
							type="button"
							className="p-[6px] rounded-[10px] text-ct-regular shrink-0"
							onClick={() => {
								setIsFileUploadModalOpen(false);
								setIsDraggingFile(false);
								dragCounterRef.current = 0;
							}}
						>
							<Icon name="Close" width={20} height={20} />
						</button>
					</HStack>

					<input
						ref={fileInputRef}
						type="file"
						className="hidden"
						onChange={(e) => {
							handleFileSelect(e.target.files);
							e.target.value = "";
						}}
					/>
					{uploadedFiles.length === 0 ? (
						<button
							type="button"
							className={`w-full rounded-[16px] transition-colors cursor-pointer ${isDraggingFile ? "bg-pm-strong/10" : "bg-bg-regular/60"}`}
							onClick={() => fileInputRef.current?.click()}
							onDrop={handleFileDrop}
							onDragOver={handleFileDragOver}
							onDragEnter={handleFileDragEnter}
							onDragLeave={handleFileDragLeave}
						>
							<VStack className="w-full py-[40px] items-center gap-[8px]">
								<Icon name="Upload" width={28} height={28} className={isDraggingFile ? "text-pm-strong" : "text-ct-regular"} />
								<span className={`text-sm ${isDraggingFile ? "text-pm-strong" : "text-ct-regular"}`}>{t("workflow.fileUploadDragDrop")}</span>
								<span className="text-xs text-ct-regular/60">{t("workflow.fileSizeGuide", { size: "20MB" })}</span>
							</VStack>
						</button>
					) : (
						<HStack className="w-full justify-between items-center px-[12px] py-[10px] rounded-[12px] bg-bg-regular/60 border-outline">
							<VStack className="items-start gap-[2px] min-w-0 flex-1">
								<span className="text-sm text-ct-strong font-medium truncate w-full">{uploadedFiles[0].name}</span>
								<span className="text-xs text-ct-regular">{(uploadedFiles[0].size / 1024).toFixed(1)} KB</span>
							</VStack>
							<button
								type="button"
								className="p-[6px] rounded-[8px] text-ct-regular shrink-0"
								onClick={() => setUploadedFiles([])}
							>
								<Icon name="Close" width={14} height={14} />
							</button>
						</HStack>
					)}

					<HStack className="w-full gap-[10px]">
						<Button
							className="w-full"
							variant="primary"
							onClick={async () => {
								setIsUploading(true);
								setUploadError(null);
								try {
									await uploadFile.mutateAsync({ workflowId: id, file: uploadedFiles[0] });
									setUploadedFiles([]);
								} catch (e) {
									setUploadError(
										e instanceof Error ? e.message : t("workflow.uploadFailed"),
									);
								} finally {
									setIsUploading(false);
								}
							}}
							disabled={uploadedFiles.length === 0 || isUploading}
						>
							{isUploading ? <Spinner size="sm" /> : t("workflow.fileUpload")}
						</Button>
					</HStack>

					{uploadError && (
						<span className="text-sm text-red-500">{uploadError}</span>
					)}

					{(uploadsData?.uploads ?? []).length > 0 && (
						<VStack className="w-full gap-[8px]">
							<VStack className="w-full gap-[6px] max-h-[240px] justify-start overflow-auto">
								{(uploadsData?.uploads ?? []).map((upload) => (
									<HStack
										key={upload.id}
										className="w-full justify-between items-center px-[12px] py-[10px] rounded-[12px] bg-bg-regular/60 border-outline cursor-pointer hover:bg-bg-regular/80 transition-colors"
										onClick={async () => {
											const result = await getDownloadUrl.mutateAsync({ workflowId: id, uploadId: upload.id });
											window.open(result.downloadUrl, "_blank");
										}}
									>
										<VStack className="items-start gap-[2px] min-w-0 flex-1">
											<span className="text-sm text-ct-strong font-medium truncate w-full">{upload.fileName}</span>
											<HStack className="gap-[8px]">
												{upload.size != null && (
													<span className="text-xs text-ct-regular">{(upload.size / 1024).toFixed(1)} KB</span>
												)}
												<span className={`text-xs ${upload.status === "READY" ? "text-pm-strong" : "text-ct-regular"}`}>
													{upload.status}
												</span>
											</HStack>
										</VStack>
										<button
											type="button"
											className="p-[6px] rounded-[8px] text-ct-regular shrink-0"
											onClick={(e) => {
												e.stopPropagation();
												deleteUpload.mutate({ workflowId: id, uploadId: upload.id });
											}}
										>
											<Icon name="Trash" width={14} height={14} />
										</button>
									</HStack>
								))}
							</VStack>
						</VStack>
					)}
				</VStack>
			</Modal>
			<Modal
				className="w-full max-w-[520px]"
				isOpen={isHelpModalOpen}
				onClose={() => setIsHelpModalOpen(false)}
			>
				<VStack className="w-full max-h-[82vh] rounded-[24px] bg-bg-weak/95 outline-outline p-[20px] gap-[14px] items-start backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
					<VStack className="w-full items-start gap-[4px] p-[4px]">
						<span className="text-xl font-semibold text-ct-strong">{t("workflow.helpTitle")}</span>
						<span className="text-sm text-ct-regular">
							{t("workflow.helpDescription")}
						</span>
					</VStack>

					<VStack className="w-full items-start justify-start gap-[8px] overflow-auto pr-[2px]">
						<VStack className="w-full items-start gap-[6px] rounded-[14px] bg-bg-regular/60 border-outline p-[12px]">
							<span className="text-sm font-semibold text-ct-strong">{t("workflow.helpStep1Title")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep1Desc1")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep1Desc2")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep1Desc3")}</span>
						</VStack>

						<VStack className="w-full items-start gap-[6px] rounded-[14px] bg-bg-regular/60 border-outline p-[12px]">
							<span className="text-sm font-semibold text-ct-strong">{t("workflow.helpStep2Title")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep2Desc1")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep2Desc2")}</span>
						</VStack>

						<VStack className="w-full items-start gap-[6px] rounded-[14px] bg-bg-regular/60 border-outline p-[12px]">
							<span className="text-sm font-semibold text-ct-strong">{t("workflow.helpStep3Title")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep3Desc1")}</span>
							<span className="text-sm text-ct-regular">{t("workflow.helpStep3Desc2")}</span>
						</VStack>
					</VStack>

					<HStack className="w-full pt-[4px]">
						<Button className="w-full" variant="primary" onClick={() => setIsHelpModalOpen(false)}>
							{t("common.confirm")}
						</Button>
					</HStack>
				</VStack>
			</Modal>

			<ConfirmModal
				isOpen={isNewErrorModalOpen}
				onCancel={() => setIsNewErrorModalOpen(false)}
				title={t("workflow.newErrorModalTitle")}
				description={t("workflow.newErrorModalDescription")}
				onConfirm={() => setIsNewErrorModalOpen(false)}
			/>
			<ConfirmModal
				isOpen={!!deleteTargetId}
				onCancel={() => setDeleteTargetId(null)}
				title={t("workflow.deleteModalTitle")}
				description={t("workflow.deleteModalDescription")}
				onConfirm={handleDeleteWorkflow}
			/>
			<ConfirmModal
				isOpen={isRepairConfirmModalOpen}
				onCancel={() => setIsRepairConfirmModalOpen(false)}
				title={t("workflow.repairModalTitle")}
				description={t("workflow.repairModalDescription")}
				onConfirm={() => {
					setIsRepairConfirmModalOpen(false);
					setIsNotImplementedModalOpen(true);
				}}
			/>
			<ConfirmModal
				isOpen={isLogoutConfirmModalOpen}
				onCancel={() => setIsLogoutConfirmModalOpen(false)}
				title={t("workflow.logoutModalTitle")}
				description={t("workflow.logoutModalDescription")}
				onConfirm={handleLogout}
			/>
			<ConfirmModal
				isOpen={isNotImplementedModalOpen}
				onCancel={() => setIsNotImplementedModalOpen(false)}
				title={t("workflow.notImplementedTitle")}
				description={t("workflow.notImplementedDescription")}
				onConfirm={() => setIsNotImplementedModalOpen(false)}
			/>
			<Modal
				className="w-full max-w-[620px]"
				isOpen={isEnvModalOpen}
				onClose={() => setIsEnvModalOpen(false)}
			>
				<VStack className="w-full max-h-[82vh] rounded-[24px] bg-bg-weak/95 border-outline p-[20px] gap-[16px] items-start backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
					<HStack className="w-full justify-between items-center p-[4px]">
						<VStack className="items-start gap-[2px]">
							<span className="text-xl font-semibold text-ct-strong">{t("workflow.envTitle")}</span>
							<span className="text-sm text-ct-regular">
								{t("workflow.envDescription")}
							</span>
						</VStack>
						<button
							type="button"
							className="p-[6px] rounded-[10px] text-ct-regular shrink-0"
							onClick={() => setIsEnvModalOpen(false)}
						>
							<Icon name="Close" width={20} height={20} />
						</button>
					</HStack>

					<div ref={envListRef} className="w-full overflow-auto">
						<VStack className="w-full items-start justify-start rounded-[16px] p-[12px] gap-[8px] border-outline">
							{envSettings.length === 0 ? (
								<VStack className="w-full py-[24px] items-center gap-[6px]">
									<span className="text-sm text-ct-regular">{t("workflow.envEmpty")}</span>
								</VStack>
							) : (
								envSettings.map((item) => (
									<HStack key={item.id} className="w-full items-end gap-[6px]">
										<div className="flex-1 min-w-0">
											<TextInputField
												label={t("workflow.envKey")}
												value={item.key}
												onChange={(event) =>
													handleUpdateEnvSetting(item.id, "key", event.target.value)
												}
												placeholder="API_KEY"
												className="w-full"
												clearable
											/>
										</div>
										<div className="flex-1 min-w-0">
											<TextInputField
												label={t("workflow.envValue")}
												value={item.value}
												onChange={(event) =>
													handleUpdateEnvSetting(item.id, "value", event.target.value)
												}
												placeholder="your-value"
												className="w-full"
												clearable
											/>
										</div>
										<button
											type="button"
											className="h-[50px] px-[12px] m-[2px] rounded-[10px] bg-bg-regular/60 text-ct-regular shrink-0"
											onClick={() => handleDeleteEnvSetting(item.id)}
										>
											<Icon name="Trash" width={16} height={16} />
										</button>
									</HStack>
								))
							)}
						</VStack>
					</div>

					<HStack className="w-full gap-[10px]">
						<Button
							className="w-full"
							onClick={handleAddEnvSetting}
						>
							<HStack className="w-full justify-center gap-[8px]">
								<Icon name="Add" width={14} height={14} />
								<span>{t("workflow.envAddRow")}</span>
							</HStack>
						</Button>
						<Button
							className="w-full"
							variant="primary"
							onClick={() => {
								const envMap: Record<string, string> = {};
								for (const row of envSettings) {
									const k = row.key.trim();
									if (k) envMap[k] = row.value;
								}
								updateEnv.mutate({ workflowId: id, request: { env: envMap } });
								setIsEnvModalOpen(false);
							}}
						>
							{t("common.confirm")}
						</Button>
					</HStack>
				</VStack>
			</Modal>
			<NodePropertyModal
				isOpen={Boolean(editingElement)}
				element={editingElement}
				elements={canvasController.elements}
				edges={canvasController.edges}
				onClose={handleCloseNodePropertyModal}
				onSave={handleSaveNodeProperty}
			/>
			<AnimatePresence>
				{(isDropdownOpen || isActionDropdownOpen || isControlDropdownOpen) && (
					<div
						className="fixed inset-0 z-10"
						onClick={() => {
							setIsDropdownOpen(false);
							setIsActionDropdownOpen(false);
							setIsControlDropdownOpen(false);
						}}
						onWheel={() => {
							setIsDropdownOpen(false);
							setIsActionDropdownOpen(false);
							setIsControlDropdownOpen(false);
						}}
						onTouchMove={() => {
							setIsDropdownOpen(false);
							setIsActionDropdownOpen(false);
							setIsControlDropdownOpen(false);
						}}
					/>
				)}
			</AnimatePresence>
			<VStack className={`w-full h-full ${isMobile ? "min-h-0" : "max-w-[440px]"} p-[20px] gap-[16px] justify-start ${isDropdownOpen ? "overflow-visible" : "overflow-auto"} shrink-0 relative ${isMobile && activeTab !== "controls" ? "hidden" : ""}`}>
				<div className="w-full relative">
					<WorkflowHeader
						selected={workflow?.name ?? ""}
						onToggleDropdown={() => setIsDropdownOpen((v) => !v)}
					/>
					<AnimatePresence>
						{isDropdownOpen && (
							<WorkflowCardDropdown
								workflows={workflows.map((w) => ({ id: w.id, name: w.name }))}
								selectedId={id}
								onSelect={(wId) => {
									setIsDropdownOpen(false);
									navigate({ to: "/workflow/$id", params: { id: wId } });
								}}
								onDelete={(wId) => {
									setIsDropdownOpen(false);
									setDeleteTargetId(wId);
								}}
								onNewWorkflow={() => {
									setIsDropdownOpen(false);
									handleNewWorkflow();
								}}
							/>
						)}
					</AnimatePresence>
				</div>
					<button
						className="w-full"
						onClick={async () => {
							if (isExecutionRunning) {
								stopExecution.mutate(id);
							} else {
								startExecution.mutate(id);
							}
						}}
						disabled={startExecution.isPending || stopExecution.isPending || hasErrors}
					>
					<HStack className={`w-full p-[16px] gap-[10px] rounded-[20px] text-md font-medium ${isExecutionRunning ? "bg-bg-regular text-ct-regular" : hasErrors ? "border border-dg-strong text-dg-strong bg-dg-weak" : "bg-pm-strong text-ct-elevated shadow-[0_8px_32px_rgba(233,107,53,0.2)]"}`}>
						<Icon name={isExecutionRunning ? "Pause" : hasErrors ? "Delete" : "Play"} width={20} height={20} />
						<span>{isExecutionRunning ? t("workflow.pause") : hasErrors ? t("workflow.error") : t("workflow.run")}</span>
					</HStack>
				</button>
				<GyulCard monthlyUsage={executionLogsData?.monthGyulUsed ?? 0} remainUsage={user?.gyulBalance ?? 0} onRefill={() => setIsRefillModalOpen(true)} />
				{(user?.plan === "PRO" || user?.plan === "ENTERPRISE") && (
				<VStack className="w-full px-[16px] py-[20px] rounded-[20px] gap-[20px] border border-outline bg-bg-weak/80 backdrop-blur-sm shadow-[0_8px_32px_rgba(15,23,42,0.06)]">
					<CardHeader
						title={t("workflow.dashboardTitle")}
						description={t("workflow.dashboardDescription")}
						buttonLabel={t("workflow.dashboardButtonLabel")}
						onBtnTap={() => navigate({ to: "/workflow/$id/dashboard", params: { id } })}
					/>
				</VStack>
				)}
				{(errorLogsData?.errors ?? []).length > 0 && (
					<ErrorCard
						errors={(errorLogsData?.errors ?? []).map((e) => {
							const moveUrl = getErrorMoveUrl(e.title, e.description, e.url);
							return {
								title: e.title,
								description: e.description,
								url: moveUrl,
								onMoveTo: moveUrl
									? () => {
											const workflowReturnUrl = `/workflow/${id}`;
											sessionStorage.setItem("oauth_return_url", workflowReturnUrl);
											if (moveUrl === SOLAPI_LINK_PATH) {
												const popupWidth = 520;
												const popupHeight = 760;
												const left = window.screenX + Math.max(0, (window.outerWidth - popupWidth) / 2);
												const top = window.screenY + Math.max(0, (window.outerHeight - popupHeight) / 2);
												const popupUrl = `${moveUrl}?returnUrl=${encodeURIComponent(workflowReturnUrl)}&popup=1`;
												const popup = window.open(
													popupUrl,
													"inflect-solapi-connect",
													`width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`,
												);
												if (popup) {
													popup.focus();
													startSolapiPopupPolling(popup, workflowReturnUrl);
													return;
												}
											}
											window.location.assign(moveUrl);
										}
									: undefined,
							};
						})}
						onRepair={() => setIsNotImplementedModalOpen(true)}
					/>
				)}
				{(executionLogsData?.logs ?? []).length > 0 && (
					<LoggerCard
						logs={(executionLogsData?.logs ?? []).map((log) => ({
							timestamp: formatRelativeTime(log.createdAt, i18n.language === "ko" ? "ko" : "en"),
							message: log.message,
							gyulAmount: log.gyulUsed,
						}))}
						isFetching={isLogsFetching}
						onRefresh={refreshLogs}
					/>
				)}
				<UserCard
					plan={user?.plan ?? "BASIC"}
					email={user?.email ?? ""}
					onLogout={() => setIsLogoutConfirmModalOpen(true)}
				/>
			</VStack>
			<VStack className={`w-full h-full ${isMobile ? "p-[12px]" : "py-[20px] pr-[20px]"} ${isMobile && activeTab !== "canvas" ? "hidden" : ""}`}>
				<VStack className="w-full h-full rounded-[20px] outline-outline relative font-medium overflow-hidden shadow-[0_8px_32px_rgba(15,23,42,0.04)]">
					<HStack className="absolute z-60 bottom-[20px] left-[20px] gap-[10px] text-ct-strong">
						<div className="relative">
							{/* <button
								ref={actionButtonRef}
								onClick={() => {
									setIsControlDropdownOpen(false);
									setIsActionDropdownOpen((prev) => !prev);
								}}
							>
								<HStack className="px-[16px] py-[12px] rounded-[12px] gap-[10px] bg-bg-weak/80 outline-outline text-sm backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)]">
									<Icon name="Action" width={18} height={18} />
									<span>{t("workflow.action")}</span>
								</HStack>
							</button> */}
							<AnimatePresence>
								{isActionDropdownOpen ? (
									<div
										ref={actionDropdownRef}
										className="absolute left-0 bottom-full mb-[10px] min-w-[240px] z-70"
									>
										<VStack
											className="items-start justify-start bg-bg-weak/95 rounded-[14px] outline-outline p-[10px] gap-[8px] max-h-[280px] overflow-auto backdrop-blur-md shadow-[0_12px_40px_rgba(15,23,42,0.1)]"
											initial={{ opacity: 0, y: 8 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: 8 }}
											transition={{ duration: 0.15, ease: "easeOut" }}
										>
											{actionModules.map((module) => (
												<div
													className="w-full"
													key={module.label}
													draggable
													onDragStart={(event) => {
														event.dataTransfer.effectAllowed = "copy";
														event.dataTransfer.setData(
															WORKFLOW_DND_TYPE,
															JSON.stringify(module.payload),
														);
														setIsActionDropdownOpen(false);
													}}
												>
													<HStack className="w-full justify-between px-[10px] py-[8px] rounded-[8px] bg-bg-regular/60 text-ct-strong text-sm font-medium cursor-grab active:cursor-grabbing">
														<span>{module.label}</span>
														<HStack className="text-ct-regular font-normal">
															<Icon name="GyulLeft" width={16} height={16} />
															<span className="text-end">{t("common.amountWithCount", { count: module.gyulAmount })}</span>
														</HStack>
													</HStack>
												</div>
											))}
										</VStack>
									</div>
								) : null}
							</AnimatePresence>
						</div>
						<div className="relative">
							{/* <button
								ref={controlButtonRef}
								onClick={() => {
									setIsActionDropdownOpen(false);
									setIsControlDropdownOpen((prev) => !prev);
								}}
							>
								<HStack className="px-[16px] py-[12px] rounded-[12px] gap-[10px] bg-bg-weak/80 outline-outline text-sm backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)]">
									<Icon name="Control" width={18} height={18} />
									<span>{t("workflow.control")}</span>
								</HStack>
							</button> */}
							<AnimatePresence>
								{isControlDropdownOpen ? (
									<div
										ref={controlDropdownRef}
										className="absolute left-0 bottom-full mb-[10px] min-w-[120px] z-70"
									>
										<VStack
											className="items-start bg-bg-weak/95 rounded-[14px] outline-outline p-[10px] gap-[8px] backdrop-blur-md shadow-[0_12px_40px_rgba(15,23,42,0.1)]"
											initial={{ opacity: 0, y: 8 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: 8 }}
											transition={{ duration: 0.15, ease: "easeOut" }}
										>
											{controlModules.map((module) => (
												<div
													className="w-full"
													key={module.label}
													draggable
													onDragStart={(event) => {
														event.dataTransfer.effectAllowed = "copy";
														event.dataTransfer.setData(
															WORKFLOW_DND_TYPE,
															JSON.stringify(module.payload),
														);
														setIsControlDropdownOpen(false);
													}}
												>
													<HStack className="w-full p-[10px] justify-between rounded-[8px] bg-bg-regular/60 text-ct-strong text-sm font-medium cursor-grab active:cursor-grabbing">
														<span>{module.label}</span>
													</HStack>
												</div>
											))}
										</VStack>
									</div>
								) : null}
							</AnimatePresence>
						</div>
					</HStack>
					{/* <HStack className="absolute z-60 bottom-[20px] right-[20px] gap-[10px]">
						<Button
							className="outline-outline bg-bg-weak/80 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)]"
							onClick={() => setIsFileUploadModalOpen(true)}
						>
							{t("workflow.fileUpload")}
						</Button>
						<Button
							className="outline-outline bg-bg-weak/80 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)]"
							onClick={() => {
								const currentEnv = _envData?.env ?? {};
								const rows = Object.entries(currentEnv).map(([key, value]) => ({
									id: typeof globalThis.crypto?.randomUUID === "function"
										? globalThis.crypto.randomUUID()
										: `env-${Date.now()}-${Math.random().toString(16).slice(2)}`,
									key,
									value,
								}));
								setEnvSettings(rows.length > 0 ? rows : []);
								setIsEnvModalOpen(true);
							}}
						>
							{t("workflow.env")}
						</Button>
						<Button
							className="outline-outline bg-bg-weak/80 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)]"
							onClick={() => setIsDatabaseModalOpen(true)}
						>
							{t("workflow.database")}
						</Button>
					</HStack> */}
					<HStack className="absolute z-60 top-[20px] right-[20px] gap-[10px] text-ct-regular">
						{[
							{ icon: "Refresh" as const, onClick: handleRefresh },
							{ icon: "Inflect" as const, onClick: () => navigate({ to: "/" }) },
						].map(({ icon, onClick }) => (
							<button
								key={icon}
								className="w-[36px] h-[36px] rounded-[12px] border-outline bg-bg-weak/80 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)]"
								onClick={onClick}
							>
								<Icon name={icon} width={20} height={20} />
							</button>
						))}
						<button
							className="w-[36px] h-[36px] rounded-[12px] border-outline bg-bg-weak/80 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.06)] text-lg"
							onClick={() => setIsHelpModalOpen(true)}
						>
							{t("common.help")}
						</button>
					</HStack>
					<WorkflowCanvas
						ref={canvasRef}
						controller={canvasController}
						onEditElement={(element) => setEditingElement(element)}
					/>
				</VStack>
			</VStack>
			{isMobile && (
				<button
					className="fixed bottom-[24px] right-[20px] z-80 w-[56px] h-[56px] rounded-full bg-pm-strong text-ct-elevated shadow-[0_8px_32px_rgba(233,107,53,0.35)] active:scale-95 transition-transform"
					onClick={() => {
						const next = activeTab === "controls" ? "canvas" : "controls";
						setActiveTab(next);
						if (next === "canvas") {
							requestAnimationFrame(() => canvasRef.current?.fitToContent());
						}
					}}
				>
					<Icon name={activeTab === "controls" ? "Control" : "Setting"} width={24} height={24} />
				</button>
			)}
		</HStack>
	);
};

export const workflowIdRoute = createRoute({
	getParentRoute: () => workflowRoute,
	path: "/$id",
	component: () => <Outlet />,
});

export const workflowIdIndexRoute = createRoute({
	getParentRoute: () => workflowIdRoute,
	path: "/",
	component: Desktop,
});
